import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const HappyValleyGooseBay = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const neighborhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/'

  const getAbout = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.happy-valley-goose-bay:About Happy Valley-Goose Bay Paragraph 1">
            Happy Valley-Goose Bay, situated in{' '}
            <LinkText href="/rental-listings/newfoundland-and-labrador">
              Newfoundland and Labrador (NL)
            </LinkText>
            , Canada, is a thriving community in central Labrador. Initially
            established as Goose Bay Air Base during World War II, it has since
            evolved into a town encompassing both Happy Valley and Goose Bay.
            With a diverse economy including transportation, natural resources,
            and services, it boasts Goose Bay Airport as a crucial transport
            hub.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.happy-valley-goose-bay:About Happy Valley-Goose Bay Paragraph 2">
            The town’s cultural and recreational offerings celebrate its
            wilderness, appealing to fishing, boating, and snowmobiling
            enthusiasts. Its population typically hovers around 7,000 to 8,000
            residents, making it a significant presence in Labrador.
            Additionally, Happy Valley-Goose Bay embraces its Indigenous
            communities, primarily the Innu and Inuit, contributing to its rich
            cultural tapestry. This vibrant town offers a gateway to explore the
            natural beauty of NL and is a noteworthy addition to{' '}
            <LinkText href={neighborhoodsLink}>
              cities and neighbourhoods in NL
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={neighborhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$600 - $1,500',
          bed2: '$900 - $1,900',
          bed3: '$1,500 - $2,500'
        }}
        rentReportCity={t('common.monthly', 'monthly')}
        rentReportLink="https://liv.rent/blog/rent-reports/"
        rentReportLinkZh="https://liv.rent/blog/zh/%e7%a7%9f%e9%87%91%e7%bb%9f%e8%ae%a1/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.happy-valley-goose-bay:Apartment Hunting in Happy Valley-Goose Bay Paragraph 1">
            Compared to cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and <LinkText href="/rental-listing/city/toronto">Toronto</LinkText>
            , it’s easier to find cheap rentals in Happy Valley-Goose Bay
            although{' '}
            <LinkText href={links.apartmentsUnder1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            may still pose as a challenge. To start your search, you can refer
            to our guides on{' '}
            <LinkText href={neighborhoodsLink}>
              neighbourhoods in Canada
            </LinkText>
            .
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.happy-valley-goose-bay:Apartments for rent Happy Valley-Goose Bay Paragraph">
              In Happy Valley-Goose Bay,{' '}
              <LinkText href={links.apartment}>apartments for rent</LinkText>{' '}
              are commonly found in areas closer to amenities and commercial
              centers. These are often suitable for singles, couples, or small
              families. The rental prices can vary significantly based on size
              and location, but they are generally considered more affordable
              than larger housing options.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.happy-valley-goose-bay:Houses for rent Happy Valley-Goose Bay Paragraph">
              Detached{' '}
              <LinkText href={links.house}>
                houses for rent in Happy Valley-Goose Bay
              </LinkText>{' '}
              offer more space and privacy, suitable for families. They are
              scattered throughout residential areas of the town, and rental
              prices can be higher, reflecting the increased living space and,
              often, yard access. Both partial houses and entire houses for rent
              are common, as well as both new builds and older homes.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.happy-valley-goose-bay:Townhouses for rent Happy Valley-Goose Bay Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Happy Valley-Goose Bay
              </LinkText>{' '}
              are less common than standalone houses. Combining the best
              qualities of both apartments and whole houses, townhouses provide
              a middle-ground solution. Typically less expensive than houses,
              townhouses for rent offer an attractive combination of freedom and
              space, generally coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Basements for rent CITY Paragraph">
              For those looking to find affordable rental housing in this
              increasingly expensive city, basements for rent in {{ cityName }}{' '}
              pose an attractive alternative. Since house prices are so
              expensive, homeowners will sometimes rent out their basements as a
              way to offset their mortgage payments. This makes it easy for
              renters to find relatively inexpensive basements for rent in all
              areas of the city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.happy-valley-goose-bay:Condos for rent Happy Valley-Goose Bay Paragraph">
              <LinkText href={links.apartment}>Condos for rent</LinkText> are
              less common in Happy Valley-Goose Bay, but they can be found in
              planned communities or newer residential areas. These types of
              rental accommodation are perfect for those who like to be in the
              centre of the action, and don’t need as much space.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.happy-valley-goose-bay:Short term rentals Happy Valley-Goose Bay Paragraph">
              Happy Valley-Goose Bay, known primarily for its strategic military
              importance and as a service center for various Labrador
              communities, offers a range of employment opportunities,
              particularly in sectors like defense, retail, and healthcare.
              While it doesn’t host extensive academic institutions, it provides
              essential educational facilities and some specialized training
              programs. The town sees a demand for various rental
              accommodations, with{' '}
              <LinkText href={links.shortTerm}>short-term rentals</LinkText>{' '}
              being an option, though not the predominant choice, especially for
              those coming for seasonal work or temporary projects. The appeal
              of short-term rentals lies in their no-commitment nature, suitable
              for transient workers or students. However, the{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                decision to opt for short-term rentals
              </LinkText>{' '}
              should be informed by individual needs, the availability of
              rentals, and local market trends.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals LOCATION 2 Paragraph">
              {{ cityName }} is known for its inexpensive rent, making it
              relatively easy to find something within your budget. If you’re
              looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ cityNameWithState }}
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in {{ cityName }}{' '}
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Trans i18nKey="seo.city.happy-valley-goose-bay:Living in Happy Valley-Goose Bay Paragraph">
          <Paragraph>
            Happy Valley-Goose Bay presents a unique experience defined by both
            the ruggedness of remote life and the sense of community. This town,
            suitable for outdoor enthusiasts and those seeking a slower pace of
            life, offers renter-friendly options, though the housing market can
            be limited due to the area’s remoteness. Known for being a
            logistical hub in Labrador, it attracts professionals, especially in
            healthcare, defense, and retail sectors, as well as students
            attending the local college. Residents and visitors alike revel in
            outdoor activities such as boating, fishing, and snowmobiling, made
            special by the breathtaking natural scenery, including the nearby
            Mealy Mountains and Lake Melville. The town’s rich cultural tapestry
            is evident in its celebrations of Indigenous and settler heritage,
            offering a glimpse into the region’s history and traditional way of
            life.
          </Paragraph>
          <Paragraph>
            If you’re not sure that Chipman is the right city for you, check out
            some of these resources comparing the different cities.
          </Paragraph>
        </Trans>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.happy-valley-goose-bay:Popular Neighbourhoods Happy Valley-Goose Bay Paragraph">
          Like any city, Happy Valley-Goose Bay is made up of smaller
          neighbourhoods with their own distinct features. Some of the most
          popular neighbourhoods include Spruce Park, Valleyview, Hamilton
          Heights, North Side, Goose Bay North, Central Happy Valley, and South
          Side. For more info about Happy Valley-Goose Bay, you can consult this{' '}
          <LinkText href={neighborhoodsLink}>
            guide on NL neighbourhoods
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.happy-valley-goose-bay:Local Landmarks Happy Valley-Goose Bay Paragraph">
          Happy Valley-Goose Bay is adorned with several landmarks that reflect
          its rich cultural heritage and natural beauty. The Labrador Military
          Museum presents a deep dive into the region’s strategic military
          importance and showcases artifacts, equipment, and stories from past
          conflicts. Not far from town, the mesmerizing Mealy Mountains and the
          vast Lake Melville offer breathtaking natural vistas and outdoor
          adventures, standing as a testament to the area’s untamed beauty. The
          historic Birch Island Boardwalk allows a peaceful walk through serene
          landscapes and offers insights into the local flora and fauna.
          Additionally, the Them Days Archives, though modest in appearance,
          holds a significant collection of Labrador’s intricate history,
          preserving the stories and heritage of the area’s Indigenous and
          settler populations.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default HappyValleyGooseBay
